import type { AuctionSettings } from "./api";

/**
 * The auction settings the panel starts from, and how it tells whether they have moved (M1.1).
 *
 * Its own module rather than an export beside the component, because two components use it and a
 * file that exports both a component and a constant loses React's fast refresh for the component.
 *
 * **These defaults are a copy**, and the original is `DEFAULT_AUCTION` in
 * `packages/core/src/shows/auctions.ts`. The panel is a separate build with no import path into
 * `core`'s domain, the same way its `Lot` type is hand-written against `lib/shows.ts` — so what
 * keeps the two honest is that both live in this repository and change together. Sending nothing
 * would also work, since the route defaults field by field; the form fills them in so the host
 * *sees* what the lot will be built with, which is `R-AUC-13`'s point one step earlier.
 */
export const DEFAULT_AUCTION: AuctionSettings = {
  minIncrement: "1.00",
  durationSeconds: 10,
  counterBidSeconds: 5,
  closeMode: "standard",
};

/**
 * What the starting price box is pre-filled with for an auction lot.
 *
 * Not the variant's Shopify price, which is what a **fixed-price** lot still defaults to and what
 * spec §7.2 describes. An auction opens at a floor and finds its price in the room (`R-AUC-7` —
 * there are no reserves), so opening at the storefront price is opening at the ceiling and asking
 * the room to beat it. £1 is the live-selling convention and is a *default*, sitting in an
 * editable box beside the Shopify figure, not a rule: a host with a £400 lot types 400.
 *
 * A string because the field is a `TextInput` and the price is a decimal the API parses — money
 * does not become a float on the way through this form.
 */
export const DEFAULT_AUCTION_PRICE = "1.00";

/** Whether a form's settings differ from what the lot holds. Null means the lot has none yet. */
export function auctionChanged(
  form: AuctionSettings,
  saved: AuctionSettings | null,
): boolean {
  if (!saved) return true;
  return (
    form.minIncrement !== saved.minIncrement ||
    form.durationSeconds !== saved.durationSeconds ||
    form.counterBidSeconds !== saved.counterBidSeconds ||
    form.closeMode !== saved.closeMode
  );
}
